import type { FC } from 'react'
import { useState } from 'react'
import { Box, Divider, IconButton, MenuItem, Select, Stack, Typography } from '@mui/material'
import AssistantRoundedIcon from '@mui/icons-material/AssistantRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { formatApiDate } from '~/shared/lib/date'
import { LuciComposer } from './LuciComposer'
import { LuciTurns } from './LuciTurns'
import { useLuciTrace } from './useLuciTrace'
import type { LuciConversation } from './LuciService'
import { isAwaitingAnswer, useAskLuci, useLuciConversations, useLuciThread } from './LuciService'

const NEW_CONVERSATION = 'new'

interface LuciPanelProps {
  personId: number
  patientId: number | null
  open: boolean
  onClose: () => void
}

// Stays mounted once opened: closing hides it, so a draft and the chosen conversation survive.
export const LuciPanel: FC<LuciPanelProps> = ({ personId, patientId, open, onClose }) => {
  const [conversationId, setConversationId] = useState<number | null>(null)
  const conversations = useLuciConversations(personId)
  const thread = useLuciThread(personId, conversationId)
  const ask = useAskLuci(personId)
  useLuciTrace(personId, conversationId)

  const messages = thread.data?.messages ?? []
  const awaitingAnswer = isAwaitingAnswer(thread.data, thread.error)
  // No send while a replay read is in flight: the generated client serves a second read of the
  // same conversation from its own React Query cache, so the running read's older state would
  // land over the turns just appended.
  const working = ask.isPending || awaitingAnswer || thread.isFetching

  // Resolves whether the question was recorded; a failure is toasted by the mutation and
  // never rejects, so a chip or button may fire this without handling.
  const send = (question: string) =>
    ask.mutateAsync({ question, conversationId }).then(
      asked => {
        setConversationId(asked.conversation.id)
        return true
      },
      () => false,
    )

  const selectConversation = (value: string) =>
    setConversationId(value === NEW_CONVERSATION ? null : Number(value))

  return (
    <Box
      role="dialog"
      aria-label="Luci"
      data-testid="luci-panel"
      onKeyDown={event => {
        if (event.key === 'Escape') onClose()
      }}
      sx={theme => ({
        position: 'absolute',
        inset: 0,
        zIndex: theme.zIndex.drawer,
        bgcolor: 'background.paper',
        display: open ? 'flex' : 'none',
        flexDirection: 'column',
      })}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', px: 2, py: 1.25 }}>
        <AssistantRoundedIcon aria-hidden sx={{ color: 'text.secondary' }} />
        <Typography sx={{ fontWeight: 600, fontSize: '1.5rem', flexGrow: 1 }}>Luci</Typography>
        <Select
          size="small"
          disabled={ask.isPending}
          value={conversationId == null ? NEW_CONVERSATION : String(conversationId)}
          onChange={event => selectConversation(event.target.value)}
          inputProps={{ 'aria-label': 'Conversation' }}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value={NEW_CONVERSATION}>New conversation</MenuItem>
          {(conversations.data ?? []).map(conversation => (
            <MenuItem key={conversation.id} value={String(conversation.id)}>
              {conversationLabel(conversation)}
            </MenuItem>
          ))}
        </Select>
        <IconButton aria-label="Close Luci" onClick={onClose} size="small">
          <CloseRoundedIcon />
        </IconButton>
      </Stack>
      <Divider />
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1.5 }}>
        {messages.length === 0 ? (
          <Typography sx={{ color: 'text.secondary', fontSize: '1.4rem' }}>
            Ask about this member&rsquo;s journey, coverage, open work or recent chat. Luci cites
            what it read.
          </Typography>
        ) : (
          <LuciTurns
            messages={messages}
            personId={personId}
            patientId={patientId}
            disabled={working}
            stalled={thread.error != null}
            onAsk={send}
            onFollowCitation={onClose}
          />
        )}
      </Box>
      <Divider />
      <LuciComposer open={open} sendDisabled={working} onSend={send} />
    </Box>
  )
}

const conversationLabel = (conversation: LuciConversation) =>
  conversation.title ??
  formatApiDate(conversation.last_activity_at, 'MMM d, h:mm a') ??
  'Conversation'
